import React from 'react';

interface DevopsAzureSettingsSkeletonProps {
  step?: number;
}

const DevopsAzureSettingsSkeleton: React.FC<DevopsAzureSettingsSkeletonProps> = ({ step = 1 }) => {
  if (step === 1) {
    return (
      <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
        <div className="space-y-6 max-w-3xl">
          {/* Main Info Card Skeleton */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 mt-2">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-64 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
              </div>
            </div>

            {/* Features List Skeleton */}
            <div className="mb-6 pl-1 space-y-3">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse mb-3"></div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse flex-shrink-0 mt-0.5"></div>
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 bg-gray-300 dark:bg-gray-600 rounded w-40 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input Section Skeleton */}
            <div className="mt-6 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Info Box Skeleton */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full animate-pulse flex-shrink-0 mt-0.5"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-48 animate-pulse"></div>
                <div className="h-3 bg-blue-100 dark:bg-blue-900 rounded w-full animate-pulse"></div>
                <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-32 animate-pulse mt-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2 skeleton
  return (
    <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
      <div className="space-y-6">
        {/* Connection Status Skeleton */}
        <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <div className="h-4 bg-green-200 dark:bg-green-800 rounded w-48 animate-pulse"></div>
          </div>
          <div className="h-7 w-24 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse"></div>
        </div>

        {/* Connected Details Skeleton */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-4 bg-white/90 dark:bg-gray-800/60 shadow-sm">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 max-w-md animate-pulse mb-4"></div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse"></div>
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Next Steps Header */}
        <div className="space-y-3 pb-8">
          <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded-md w-64 animate-pulse mb-3"></div>
          <div className="grid grid-cols-1 gap-4">
            
            {/* Import Projects Box Skeleton */}
            <div className="rounded-xl border border-blue-200 dark:border-blue-800 p-5 bg-white dark:bg-gray-800/60 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse"></div>
                <div className="space-y-1.5">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-48 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
                </div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="space-y-1.5 flex-1">
                      <div className="h-3.5 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-56 animate-pulse"></div>
                    </div>
                    <div className="h-8 w-32 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse shrink-0"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Analysis Box Skeleton */}
            <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-md animate-pulse"></div>
                <div className="space-y-1.5">
                  <div className="h-4 bg-blue-300 dark:bg-blue-700 rounded w-48 animate-pulse"></div>
                  <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-64 animate-pulse"></div>
                </div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-3 bg-white/80 dark:bg-slate-800/80 rounded-lg border border-blue-100 dark:border-slate-700">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-48 mb-2 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevopsAzureSettingsSkeleton;